import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import DraftManager from '../components/DraftManager';
import type { Submission } from '../types';

// Submissions live in their own table now, so only these four statuses appear
// here. An approved request links out to the archive it became.
const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Awaiting review',
  rejected: 'Not approved',
  approved: 'Approved — archived',
};

function statusClass(s: string): string {
  if (s === 'submitted') return 'badge-pending';
  if (s === 'rejected') return 'badge-error';
  if (s === 'approved') return 'badge-ok';
  return 'badge-muted';
}

export default function MySubmissions() {
  const [rows, setRows] = useState<Submission[] | null>(null);

  useEffect(() => {
    api<{ data: Submission[] }>('/my/submissions')
      .then((r) => setRows(r.data))
      .catch(() => setRows([]));
  }, []);

  function updateRow(s: Submission) {
    setRows((prev) => prev?.map((r) => (r.id === s.id ? { ...r, ...s } : r)) ?? prev);
  }
  function removeRow(id: number) {
    setRows((prev) => prev?.filter((r) => r.id !== id) ?? prev);
  }

  const drafts = rows?.filter((r) => r.status === 'draft') ?? [];
  const others = rows?.filter((r) => r.status !== 'draft') ?? [];

  return (
    <div className="card">
      <h2>Your submissions</h2>
      <p className="muted" style={{ fontSize: 13, marginTop: -4 }}>
        Sites you've proposed for archiving.{' '}
        <Link to="/submit">Submit another →</Link>
      </p>

      {rows === null ? (
        <p className="muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="muted">
          You haven't submitted any sites yet.{' '}
          <Link to="/submit">Submit one →</Link>
        </p>
      ) : (
        <>
          {drafts.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ marginBottom: 8 }}>Drafts — finish setting up</h3>
              {drafts.map((d) => (
                <DraftManager key={d.id} draft={d} onChange={updateRow} onDeleted={removeRow} />
              ))}
            </div>
          )}

          {others.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Reviewed</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {others.map((row) => (
                  <tr key={row.id}>
                    <td>
                      {row.status === 'approved' && row.archive_id ? (
                        <Link to={`/archives/${row.archive_id}`}>{row.domain}</Link>
                      ) : (
                        <span style={{ wordBreak: 'break-word' }}>{row.domain}</span>
                      )}
                      {row.domain_verified_at && (
                        <span className="status-pill" title="Domain ownership verified via DNS"
                          style={{ marginLeft: 6, background: '#dbeafe', color: '#1e40af', fontSize: 11 }}>
                          ✓ verified
                        </span>
                      )}
                      <div className="muted" style={{ fontSize: 12, wordBreak: 'break-all' }}>
                        {row.seed_url}
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${statusClass(row.status)}`}>
                        {STATUS_LABEL[row.status] ?? row.status}
                      </span>
                    </td>
                    <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                      {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                    </td>
                    <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                      {row.reviewed_at ? new Date(row.reviewed_at).toLocaleString() : '—'}
                    </td>
                    <td style={{ maxWidth: 360, wordBreak: 'break-word' }}>
                      {row.status === 'rejected' && row.rejection_reason ? (
                        <span className="error">{row.rejection_reason}</span>
                      ) : row.submission_note ? (
                        <span className="muted">{row.submission_note}</span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}
